/**
 * Slices HTML content by visible character count while keeping tags intact.
 * This allows typewriter animation to work with formatted HTML text.
 *
 * @param html - The full HTML string
 * @param charCount - Number of visible characters to show
 * @returns HTML string with only the first `charCount` visible characters, with proper tag closure
 */
export function sliceHtmlByCharCount(html: string, charCount: number): string {
  if (charCount <= 0) return '';
  if (!html) return '';

  let visibleCount = 0;
  let result = '';
  let i = 0;
  const openTags: string[] = [];

  while (i < html.length && visibleCount < charCount) {
    if (html[i] === '<') {
      // Find the end of the tag
      const tagEnd = html.indexOf('>', i);
      if (tagEnd === -1) {
        // Malformed HTML, just add the character
        result += html[i];
        i++;
        continue;
      }

      const tag = html.slice(i, tagEnd + 1);
      result += tag;

      // Check if it's a closing tag, self-closing tag, or opening tag
      if (tag.startsWith('</')) {
        // Closing tag - remove from open tags stack
        const tagName = tag.slice(2, -1).split(/\s/)[0].toLowerCase();
        const lastIndex = openTags.lastIndexOf(tagName);
        if (lastIndex !== -1) {
          openTags.splice(lastIndex, 1);
        }
      } else if (!tag.endsWith('/>') && !isSelfClosingTag(tag)) {
        // Opening tag - add to stack
        const tagName = tag.slice(1).split(/[\s>]/)[0].toLowerCase();
        if (tagName && !isVoidElement(tagName)) {
          openTags.push(tagName);
        }
      }

      i = tagEnd + 1;
    } else if (html[i] === '&') {
      // HTML entity - count as one visible character
      const entityEnd = html.indexOf(';', i);
      if (entityEnd !== -1 && entityEnd - i < 10) {
        // Reasonable entity length
        const entity = html.slice(i, entityEnd + 1);
        result += entity;
        visibleCount++;
        i = entityEnd + 1;
      } else {
        // Not a valid entity, treat as regular character
        result += html[i];
        visibleCount++;
        i++;
      }
    } else {
      // Regular character
      result += html[i];
      visibleCount++;
      i++;
    }
  }

  // Close any remaining open tags in reverse order
  for (let j = openTags.length - 1; j >= 0; j--) {
    result += `</${openTags[j]}>`;
  }

  return result;
}

/**
 * Check if a tag is self-closing (ends with />)
 */
function isSelfClosingTag(tag: string): boolean {
  return tag.endsWith('/>');
}

/**
 * Check if a tag name is a void element (no closing tag needed)
 */
function isVoidElement(tagName: string): boolean {
  const voidElements = [
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'link',
    'meta',
    'param',
    'source',
    'track',
    'wbr',
  ];
  return voidElements.includes(tagName.toLowerCase());
}

/**
 * Count visible characters in HTML (excluding tags)
 */
export function countVisibleChars(html: string): number {
  if (!html) return 0;

  let count = 0;
  let i = 0;

  while (i < html.length) {
    if (html[i] === '<') {
      const tagEnd = html.indexOf('>', i);
      if (tagEnd !== -1) {
        i = tagEnd + 1;
        continue;
      }
    } else if (html[i] === '&') {
      const entityEnd = html.indexOf(';', i);
      if (entityEnd !== -1 && entityEnd - i < 10) {
        count++;
        i = entityEnd + 1;
        continue;
      }
    }
    count++;
    i++;
  }

  return count;
}
